import React, { useMemo, useState, useEffect } from "react";
import {
  BookOpen,
  Plus,
  Trash2,
  Pencil,
  Save,
  ArrowLeft,
  ChevronRight,
  Sparkles,
  X,
} from "lucide-react";

import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

import {
  BUILT_IN_DISEASES,
  readCustomDiseases,
  saveCustomDisease,
  deleteCustomDisease,
  isCustomDisease,
  slugId,
  blankDisease,
  calcDose,
} from "../data/prescriptions";

const ROUTE_OPTIONS = ["PO", "IV", "SC", "IM", "OTM", "Topical", "OU", "OD", "OS", "IV CRI"];
const FREQ_OPTIONS = ["SID", "BID", "TID", "QID", "EOD", "Weekly", "Monthly", "PRN", "Once"];
const SPECIES_OPTIONS = [
  { value: "canine", label: "Canine" },
  { value: "feline", label: "Feline" },
];

/**
 * Full CRUD library view. Left column = disease list (built-in + custom).
 * Right column = editor (only enabled for custom diseases).
 * Built-in diseases are read-only but can be "cloned" into an editable copy.
 */
export default function DiseaseLibraryEditor({ onBack }) {
  const [customList, setCustomList] = useState(() => readCustomDiseases());
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setCustomList(readCustomDiseases());
  }, []);

  const combined = useMemo(() => {
    const customIds = new Set(customList.map((d) => d.id));
    const base = BUILT_IN_DISEASES.filter((d) => !customIds.has(d.id));
    return [...base, ...customList].sort((a, b) => a.name.localeCompare(b.name));
  }, [customList]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return combined;
    return combined.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.category || "").toLowerCase().includes(q) ||
        d.species.join(" ").includes(q)
    );
  }, [combined, filter]);

  const isEditingCustom = draft && draft.id && (draft.__custom || isCustomDisease(draft.id));

  const startNew = () => {
    const d = blankDisease();
    setDraft(d);
    setSelectedId(null);
  };

  const cloneToCustom = (disease) => {
    const clone = JSON.parse(JSON.stringify(disease));
    clone.name = `${disease.name} (copy)`;
    clone.id = slugId(clone.name) + "-" + Date.now().toString(36).slice(-4);
    clone.__custom = true;
    setDraft(clone);
    setSelectedId(clone.id);
    toast.info("Editing a copy — original stays untouched.");
  };

  const selectExisting = (id) => {
    setSelectedId(id);
    const found = combined.find((d) => d.id === id);
    if (!found) return;
    if (isCustomDisease(id)) {
      setDraft(JSON.parse(JSON.stringify(found)));
    } else {
      setDraft(null); // built-in — read-only preview
    }
  };

  const handleSave = () => {
    if (!draft) return;
    if (!draft.name.trim()) return toast.error("Disease name is required.");
    if (!draft.medications.some((m) => m.drug && m.drug.trim())) {
      return toast.error("Add at least one medication.");
    }
    const finalId = draft.id && draft.id.trim() ? draft.id : slugId(draft.name);
    const saved = saveCustomDisease({ ...draft, id: finalId });
    setCustomList(readCustomDiseases());
    setSelectedId(saved.id);
    setDraft(saved);
    toast.success("Disease saved to library", { description: saved.name });
  };

  const handleDelete = (id) => {
    if (!isCustomDisease(id)) return toast.error("Built-in diseases can't be deleted (clone instead).");
    deleteCustomDisease(id);
    setCustomList(readCustomDiseases());
    if (selectedId === id) {
      setSelectedId(null);
      setDraft(null);
    }
    toast.success("Custom disease removed");
  };

  const selectedDisease =
    selectedId && !draft ? combined.find((d) => d.id === selectedId) : null;

  return (
    <div
      data-testid="disease-library-editor"
      className="min-h-screen bg-[#F9F8F6] text-[#1A1F1D]"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      {/* Header */}
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-rx"
              variant="ghost"
              onClick={onBack}
              className="rounded-md hover:bg-[#0F4C3A]/5 hover:text-[#0F4C3A]"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Rx
            </Button>
            <Separator orientation="vertical" className="h-6 bg-[#E2E8F0]" />
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#0F4C3A]" />
              <div
                className="text-lg tracking-tight"
                style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 700 }}
              >
                Disease Library
              </div>
              <Badge variant="outline" className="border-[#E2E8F0] text-[10px] font-normal ml-1">
                {combined.length} conditions
              </Badge>
            </div>
          </div>
          <Button
            data-testid="new-disease-btn"
            onClick={startNew}
            className="rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add new disease
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* List */}
          <aside className="lg:col-span-4">
            <Input
              data-testid="library-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search disease, category, species…"
              className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A] mb-3"
            />
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B66] font-semibold px-1 py-2">
              {filtered.length} match{filtered.length === 1 ? "" : "es"}
            </div>
            <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-1.5">
              {filtered.map((d) => {
                const active = selectedId === d.id || draft?.id === d.id;
                const custom = isCustomDisease(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    data-testid={`lib-row-${d.id}`}
                    onClick={() => selectExisting(d.id)}
                    className={`w-full text-left rounded-md border px-3 py-2.5 transition-colors ${
                      active
                        ? "border-[#0F4C3A] bg-[#0F4C3A]/5"
                        : "border-[#E2E8F0] bg-white hover:border-[#0F4C3A]/40"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium truncate">{d.name}</div>
                        <div className="text-[11px] text-[#5C6B66] mt-0.5 truncate">
                          {d.category} · {d.species.join(", ")} · {d.medications?.length || 0} drugs · {d.diagnostics?.length || 0} tests
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {custom && (
                          <Badge className="bg-[#0F4C3A] text-white text-[10px] font-normal">Custom</Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-[#5C6B66]" />
                      </div>
                    </div>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="text-center text-sm text-[#5C6B66] py-10 border border-dashed border-[#E2E8F0] rounded-md">
                  No match. Try clearing the filter or click <strong>Add new disease</strong>.
                </div>
              )}
            </div>
          </aside>

          {/* Editor / Preview */}
          <section className="lg:col-span-8">
            {draft ? (
              <DiseaseEditor
                draft={draft}
                setDraft={setDraft}
                onSave={handleSave}
                onDelete={() => handleDelete(draft.id)}
                onCancel={() => {
                  setDraft(null);
                  setSelectedId(null);
                }}
                isEditingCustom={isEditingCustom}
              />
            ) : selectedDisease ? (
              <DiseasePreview
                disease={selectedDisease}
                onEditCopy={() => cloneToCustom(selectedDisease)}
              />
            ) : (
              <EmptyPane onStart={startNew} />
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

/* -------------------------------- editor -------------------------------- */

function DiseaseEditor({ draft, setDraft, onSave, onDelete, onCancel, isEditingCustom }) {
  const patch = (p) => setDraft({ ...draft, ...p });
  const patchMed = (i, p) =>
    patch({ medications: draft.medications.map((m, idx) => (idx === i ? { ...m, ...p } : m)) });
  const patchDx = (i, p) =>
    patch({ diagnostics: draft.diagnostics.map((t, idx) => (idx === i ? { ...t, ...p } : t)) });

  const toggleSpecies = (val) => {
    const set = new Set(draft.species);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    if (set.size === 0) return; // must have at least one
    patch({ species: Array.from(set) });
  };

  return (
    <Card className="border-[#E2E8F0] rounded-xl">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B66] font-semibold">
              {isEditingCustom ? "Editing custom disease" : "New disease"}
            </div>
            <div className="text-xl tracking-tight mt-1" style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}>
              {draft.name || "Untitled disease"}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="editor-cancel"
              variant="outline"
              onClick={onCancel}
              className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
            >
              Cancel
            </Button>
            {isEditingCustom && (
              <Button
                data-testid="editor-delete"
                variant="outline"
                onClick={onDelete}
                className="rounded-md border-[#E53E3E]/40 text-[#E53E3E] hover:bg-[#E53E3E]/10"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            )}
            <Button
              data-testid="editor-save"
              onClick={onSave}
              className="rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white"
            >
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-8">
            <MicroLabel>Disease name</MicroLabel>
            <Input
              data-testid="editor-name"
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="e.g. Feline Herpesvirus-1 Keratitis"
              className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
            />
          </div>
          <div className="col-span-4">
            <MicroLabel>Category</MicroLabel>
            <Input
              data-testid="editor-category"
              value={draft.category}
              onChange={(e) => patch({ category: e.target.value })}
              placeholder="Ophthalmology"
              className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
            />
          </div>
          <div className="col-span-12">
            <MicroLabel>Species</MicroLabel>
            <div className="flex gap-2">
              {SPECIES_OPTIONS.map((s) => {
                const active = draft.species.includes(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    data-testid={`editor-species-${s.value}`}
                    onClick={() => toggleSpecies(s.value)}
                    className={`px-4 py-2 rounded-md text-sm border transition-colors ${
                      active
                        ? "border-[#0F4C3A] bg-[#0F4C3A] text-white"
                        : "border-[#E2E8F0] bg-white text-[#1A1F1D] hover:border-[#0F4C3A]/60"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-12">
            <MicroLabel>Clinical signs (short summary)</MicroLabel>
            <Textarea
              data-testid="editor-signs"
              value={draft.clinicalSigns}
              onChange={(e) => patch({ clinicalSigns: e.target.value })}
              placeholder="Key clinical presentation — signs, history, at-risk breeds…"
              className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A] min-h-[80px]"
            />
          </div>
        </div>

        {/* Diagnostics */}
        <SectionBlock
          title="Diagnostics"
          onAdd={() =>
            patch({
              diagnostics: [...(draft.diagnostics || []), { test: "", finding: "" }],
            })
          }
          addLabel="Add test"
          addTestId="editor-add-dx"
          empty="No diagnostics yet."
        >
          {(draft.diagnostics || []).map((t, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start">
              <Input
                data-testid={`editor-dx-test-${i}`}
                value={t.test}
                onChange={(e) => patchDx(i, { test: e.target.value })}
                placeholder="Test (e.g. Fluorescein stain)"
                className="col-span-4 border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              <Input
                data-testid={`editor-dx-finding-${i}`}
                value={t.finding}
                onChange={(e) => patchDx(i, { finding: e.target.value })}
                placeholder="Expected / interpretation"
                className="col-span-7 border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              <RemoveButton
                testId={`editor-dx-delete-${i}`}
                onClick={() => patch({ diagnostics: draft.diagnostics.filter((_, idx) => idx !== i) })}
              />
            </div>
          ))}
        </SectionBlock>

        {/* Differentials */}
        <SectionBlock
          title="Differentials to rule out"
          onAdd={() => patch({ differentials: [...(draft.differentials || []), ""] })}
          addLabel="Add DDx"
          addTestId="editor-add-ddx"
          empty="No differentials yet."
        >
          {(draft.differentials || []).map((d, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-[#5C6B66] text-xs mt-2.5 w-4 text-right">{i + 1}.</span>
              <Input
                data-testid={`editor-ddx-${i}`}
                value={d}
                onChange={(e) =>
                  patch({ differentials: draft.differentials.map((v, idx) => (idx === i ? e.target.value : v)) })
                }
                placeholder="Differential diagnosis"
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              <RemoveButton
                testId={`editor-ddx-delete-${i}`}
                onClick={() => patch({ differentials: draft.differentials.filter((_, idx) => idx !== i) })}
              />
            </div>
          ))}
        </SectionBlock>

        {/* Medications */}
        <SectionBlock
          title="Medications"
          onAdd={() =>
            patch({
              medications: [
                ...(draft.medications || []),
                { drug: "", doseMgPerKg: null, doseText: "", route: "PO", frequency: "SID", duration: "", notes: "" },
              ],
            })
          }
          addLabel="Add drug"
          addTestId="editor-add-med"
          empty="No medications yet."
        >
          {(draft.medications || []).map((m, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-2 rounded-md border border-[#E2E8F0] p-3 bg-[#F9F8F6]/60"
            >
              <div className="col-span-12 md:col-span-4">
                <MicroLabel>Drug</MicroLabel>
                <Input
                  data-testid={`editor-med-drug-${i}`}
                  value={m.drug}
                  onChange={(e) => patchMed(i, { drug: e.target.value })}
                  placeholder="Drug name"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <MicroLabel>mg/kg</MicroLabel>
                <Input
                  data-testid={`editor-med-mgkg-${i}`}
                  type="number"
                  step="0.01"
                  value={m.doseMgPerKg ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    const num = v === "" ? null : Number(v);
                    patchMed(i, {
                      doseMgPerKg: num,
                      doseText: num != null ? `${num} mg/kg` : m.doseText,
                    });
                  }}
                  placeholder="—"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                />
                {m.doseMgPerKg != null && (
                  <div className="text-[10px] mt-1 text-[#0F4C3A] font-medium">
                    ≈ {calcDose(m.doseMgPerKg, 10)} per 10 kg
                  </div>
                )}
              </div>
              <div className="col-span-6 md:col-span-2">
                <MicroLabel>Dose label</MicroLabel>
                <Input
                  data-testid={`editor-med-dosetext-${i}`}
                  value={m.doseText}
                  onChange={(e) => patchMed(i, { doseText: e.target.value })}
                  placeholder="e.g. 5 mg/kg"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                />
              </div>
              <div className="col-span-4 md:col-span-1">
                <MicroLabel>Route</MicroLabel>
                <Select value={m.route} onValueChange={(v) => patchMed(i, { route: v })}>
                  <SelectTrigger data-testid={`editor-med-route-${i}`} className="border-[#E2E8F0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROUTE_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-4 md:col-span-1">
                <MicroLabel>Freq.</MicroLabel>
                <Select value={m.frequency} onValueChange={(v) => patchMed(i, { frequency: v })}>
                  <SelectTrigger data-testid={`editor-med-freq-${i}`} className="border-[#E2E8F0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQ_OPTIONS.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-4 md:col-span-2">
                <MicroLabel>Duration</MicroLabel>
                <Input
                  data-testid={`editor-med-duration-${i}`}
                  value={m.duration}
                  onChange={(e) => patchMed(i, { duration: e.target.value })}
                  placeholder="7 days"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                />
              </div>
              <div className="col-span-11">
                <MicroLabel>Notes</MicroLabel>
                <Input
                  data-testid={`editor-med-notes-${i}`}
                  value={m.notes}
                  onChange={(e) => patchMed(i, { notes: e.target.value })}
                  placeholder="Give with food, monitor renal function…"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                />
              </div>
              <div className="col-span-1 flex items-end justify-end">
                <RemoveButton
                  testId={`editor-med-delete-${i}`}
                  onClick={() => patch({ medications: draft.medications.filter((_, idx) => idx !== i) })}
                />
              </div>
            </div>
          ))}
        </SectionBlock>

        {/* Care */}
        <SectionBlock
          title="Care & follow-up"
          onAdd={() => patch({ supportiveCare: [...(draft.supportiveCare || []), ""] })}
          addLabel="Add note"
          addTestId="editor-add-care"
          empty="No notes."
        >
          {(draft.supportiveCare || []).map((c, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-[#5C6B66] text-xs mt-2.5 w-4 text-right">{i + 1}.</span>
              <Input
                data-testid={`editor-care-${i}`}
                value={c}
                onChange={(e) =>
                  patch({ supportiveCare: draft.supportiveCare.map((v, idx) => (idx === i ? e.target.value : v)) })
                }
                placeholder="Care instruction"
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              <RemoveButton
                testId={`editor-care-delete-${i}`}
                onClick={() => patch({ supportiveCare: draft.supportiveCare.filter((_, idx) => idx !== i) })}
              />
            </div>
          ))}
        </SectionBlock>
      </CardContent>
    </Card>
  );
}

/* ------------------------------ preview view ------------------------------ */

function DiseasePreview({ disease, onEditCopy }) {
  return (
    <Card className="border-[#E2E8F0] rounded-xl">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Badge variant="outline" className="border-[#E2E8F0] text-[10px] font-normal">
              Built-in (read only)
            </Badge>
            <div className="text-2xl mt-2 tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}>
              {disease.name}
            </div>
            <div className="text-xs text-[#5C6B66] mt-1">
              {disease.category} · {disease.species.join(", ")}
            </div>
          </div>
          <Button
            data-testid="clone-to-custom"
            onClick={onEditCopy}
            className="rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white"
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Edit a copy
          </Button>
        </div>
        {disease.clinicalSigns && (
          <p className="text-sm text-[#5C6B66] mt-3 leading-relaxed">{disease.clinicalSigns}</p>
        )}
        <PreviewBlock title="Diagnostics">
          {disease.diagnostics?.length ? (
            <ul className="text-sm space-y-1.5">
              {disease.diagnostics.map((t, i) => (
                <li key={i}>
                  <span className="font-medium">{t.test}</span>
                  {t.finding && <span className="text-[#5C6B66]"> — {t.finding}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <PreviewEmpty />
          )}
        </PreviewBlock>
        <PreviewBlock title="Differentials">
          {disease.differentials?.length ? (
            <div className="flex flex-wrap gap-2">
              {disease.differentials.map((d, i) => (
                <span key={i} className="text-[12px] px-2.5 py-1 rounded-full border border-[#E2E8F0] bg-[#F9F8F6]">
                  {d}
                </span>
              ))}
            </div>
          ) : (
            <PreviewEmpty />
          )}
        </PreviewBlock>
        <PreviewBlock title="Medications">
          {disease.medications?.length ? (
            <ul className="text-sm space-y-1.5">
              {disease.medications.map((m, i) => (
                <li key={i}>
                  <span className="font-medium">{m.drug}</span>{" "}
                  <span className="text-[#5C6B66]">— {m.doseText} · {m.route} · {m.frequency} · {m.duration}</span>
                </li>
              ))}
            </ul>
          ) : (
            <PreviewEmpty />
          )}
        </PreviewBlock>
      </CardContent>
    </Card>
  );
}

function EmptyPane({ onStart }) {
  return (
    <Card className="border-dashed border-[#E2E8F0] rounded-xl">
      <CardContent className="p-10 text-center flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-[#0F4C3A]/10 grid place-items-center mb-4">
          <Sparkles className="w-5 h-5 text-[#0F4C3A]" />
        </div>
        <div className="text-2xl tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}>
          Curate your clinic&apos;s own formulary
        </div>
        <p className="text-sm text-[#5C6B66] mt-2 max-w-md">
          Pick any built-in disease on the left to review it, or hit <strong>Add new disease</strong> to build one from scratch.
          Your custom entries are saved on this device and appear in the main Rx generator immediately.
        </p>
        <Button
          data-testid="empty-add-btn"
          onClick={onStart}
          className="mt-5 rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add new disease
        </Button>
      </CardContent>
    </Card>
  );
}

/* -------------------------------- helpers -------------------------------- */

function MicroLabel({ children }) {
  return (
    <Label className="text-[10px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold block mb-1">
      {children}
    </Label>
  );
}

function SectionBlock({ title, children, onAdd, addLabel, addTestId, empty }) {
  const hasChildren = React.Children.count(children) > 0;
  return (
    <div className="pt-4 border-t border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-3">
        <div
          className="text-sm tracking-[0.12em] uppercase"
          style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
        >
          {title}
        </div>
        <Button
          data-testid={addTestId}
          variant="outline"
          size="sm"
          onClick={onAdd}
          className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> {addLabel}
        </Button>
      </div>
      <div className="space-y-2">
        {hasChildren ? (
          children
        ) : (
          <div className="text-center text-[12px] text-[#5C6B66] py-3 border border-dashed border-[#E2E8F0] rounded-md">
            {empty}
          </div>
        )}
      </div>
    </div>
  );
}

function RemoveButton({ onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-label="Remove"
      className="h-9 w-9 shrink-0 grid place-items-center rounded-md border border-[#E2E8F0] text-[#5C6B66] hover:text-[#E53E3E] hover:border-[#E53E3E]/60 transition-colors"
    >
      <X className="w-4 h-4" />
    </button>
  );
}

function PreviewBlock({ title, children }) {
  return (
    <div className="mt-5">
      <div
        className="text-[11px] uppercase tracking-[0.15em] text-[#5C6B66] font-semibold mb-2"
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function PreviewEmpty() {
  return <div className="text-xs text-[#5C6B66] italic">Not specified.</div>;
}
